import React, { useState, useMemo } from 'react';
import Button from '../../../ui/Button';
import { SearchInput } from '../../../ui/Input';

export default function UserSelectStep({ users, selectedUsers, enumNotSupported, onUpdate, onNext }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set(selectedUsers || []));

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleUser = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredUsers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredUsers.map(u => u.id || u.email)));
    }
  };

  const handleContinue = () => {
    const selectedIds = Array.from(selected);
    const details = users.filter(u => selected.has(u.id || u.email));
    onUpdate(selectedIds, details);
    onNext();
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Users</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Choose which users to migrate
        </p>
      </div>

      {enumNotSupported && (
        <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Only the authenticated account is listed. To migrate multiple users, go back and choose <strong>Bulk Migration (CSV)</strong>.
          </p>
        </div>
      )}

      {/* Search & Select All */}
      <div className="flex items-center justify-between">
        <SearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search users..."
          className="w-56"
        />
        <button
          onClick={toggleAll}
          className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400"
        >
          {selected.size === filteredUsers.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* User List */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
        {filteredUsers.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {search ? 'No matching users found' : 'No users available'}
          </div>
        ) : (
          filteredUsers.map(user => {
            const userId = user.id || user.email;
            const isSelected = selected.has(userId);
            return (
              <label
                key={userId}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  isSelected ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleUser(userId)}
                  className="w-4 h-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.email}
                  </p>
                  {user.displayName && user.displayName !== user.email && (
                    <p className="text-xs text-gray-500 truncate">{user.displayName}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400">{user.domain}</span>
              </label>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">
          {selected.size} of {users.length} users selected
        </span>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={selected.size === 0}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
